import React, { useEffect, useState } from "react";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TeamManagementService } from "@application/useCases/workspace/TeamManagementService";
import { ListTeamsService } from "@application/useCases/workspace/ListTeamsService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import { ConfirmDeleteDialog } from "@presentation/components/common/ConfirmDeleteDialog";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface DeleteTeamDialogProps {
  teamId: string;
  teamName: string;
  onClose: () => void;
  onSuccess: () => void;
}

/**
A team can only be deleted once it owns zero projects (`Teams.CannotDeleteTeamWithProjects`) - this
fetches the team's own project inventory up front so the blocked reason is explained inline instead
of only surfacing as a server error after the user already clicked Delete.
*/
export const DeleteTeamDialog: React.FC<DeleteTeamDialogProps> = ({ teamId, teamName, onClose, onSuccess }) => {
  const teamManagementService = useInjection(TeamManagementService);
  const listTeamsService = useInjection(ListTeamsService);
  const { pushNotification } = useNotifications();
  const notifySuccess = useWorkspaceSuccessNotification();
  const [projectNames, setProjectNames] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let isCancelled = false;
    void (async () => {
      try {
        const inventory = await listTeamsService.getTeamProjectInventoryAsync(teamId);
        if (!isCancelled) setProjectNames(inventory.map((project) => project.name));
      } catch {
        if (!isCancelled) setProjectNames([]);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [teamId, listTeamsService]);

  const handleConfirm = async () => {
    setIsDeleting(true);
    try {
      await teamManagementService.deleteTeamAsync(teamId);
      notifySuccess("team", "deleted");
      onSuccess();
      onClose();
    } catch (error) {
      pushNotification(extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not delete this team.", "error", 10_000);
    } finally {
      setIsDeleting(false);
    }
  };

  const isBlocked = (projectNames?.length ?? 0) > 0;

  return (
    <ConfirmDeleteDialog title="Delete team" isBlocked={isBlocked} isDeleting={isDeleting} onCancel={onClose} onConfirm={handleConfirm}>
      <p style={{ margin: 0 }}>
        Delete team <strong>{teamName}</strong>? This cannot be undone.
      </p>
      {projectNames === null ? (
        <p style={{ margin: 0, color: "#64748b" }}>Checking this team&apos;s projects...</p>
      ) : (
        isBlocked && (
          <div>
            <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#7f1d1d" }}>
              This team still owns {projectNames.length} project{projectNames.length === 1 ? "" : "s"} and can&apos;t be deleted until they&apos;re moved or deleted:
            </p>
            <ul style={{ margin: 0, paddingLeft: 20, color: "#7f1d1d" }}>
              {projectNames.map((name) => (
                <li key={name}>{name}</li>
              ))}
            </ul>
          </div>
        )
      )}
    </ConfirmDeleteDialog>
  );
};
